const _ = require("lodash");
const CR = require("./util/Cursor");
const AT = require("./ActionTypes");
const {getIn, extendIn} = require("../../../shared/ImmHelpers");

// This file is an alternate version of Slots.js which can store multiple
// pieces of data in a single slot. Each piece of data is associated to a
// stringable ID. When you ask it to load IDs, it will skip the ones which
// have data in place already.

// The shape of the mslot is
// {
//   [id]: { // multiple
//     status: "none" | "pending" | "done" | "error",
//     data: null | any,
//     error: null | string,
//   },
// }

// To use it, make a new request action in ActionTypes.js. Then add an entry
// to the table variable below that returns an object with
// - defaultValue: optional, defaults to null. The data to use before anything
//   is loaded.
// - keyList: where in the state tree the data and loading state is stored.
// - url: the endpoint to use for a loading request.
// - payload: optional, defaults to {}. The base payload to send with a
//   loading request, not including the IDs to load in that request.
// - payloadIDsKey: the key under which to store the array of IDs for
//   that request. This key is added to the payload object in requests.
// - action: the request action; its success and fail variants are used for
//   responses to the loading request.
// See the handlers of existing slot actions for examples of intended usage.

// TODO: We may be able to improve the data structure here to include features
// like pagination (breaking requests up into pieces) and pruning. Then it can
// generalize the library pages loading things and maybe more.

const table = {
	// IDs are classIDs
	reportClasses() {
		return {
			keyList: ["school", "report", "classes"],
			payloadIDsKey: "classIDs",
			url: "/api/school/getClasses",
			action: AT.LOAD_REPORT_CLASSES_SLOT_REQUEST,
		};
	},

	// IDs are studentIDs
	reportStudents(classID) {
		return {
			keyList: ["school", "report", "students"],
			payload: {classID},
			payloadIDsKey: "studentIDs",
			url: "/api/school/getStudents",
			action: AT.LOAD_REPORT_STUDENTS_SLOT_REQUEST,
		};
	},

	// IDs are student IDs.
	reportGradeChapter() {
		return {
			keyList: ["report", "gradeChapter"],
			payloadIDsKey: "studentIDs",
			url: "/api/report/getGradeChapter",
			action: AT.LOAD_REPORT_GRADE_CHAPTER_SLOT_REQUEST,
		};
	},

	// IDs are student IDs.
	reportChapterBlock(chapterID, blockIDs) {
		return {
			keyList: ["report", "chapterBlock", chapterID],
			payload: {chapterID, blockIDs},
			payloadIDsKey: "studentIDs",
			url: "/api/report/getChapterBlock",
			action: AT.LOAD_REPORT_CHAPTER_BLOCK_SLOT_REQUEST,
		};
	},

	// IDs are student IDs.
	reportBlockResults(chapterID, blockNumber) {
		const keyList = ["report", "blockResults", chapterID];
		if (blockNumber) keyList.push(blockNumber);
		return {
			keyList,
			payload: {chapterID, blockNumber},
			payloadIDsKey: "studentIDs",
			url: "/api/report/getBlockResults",
			action: AT.LOAD_REPORT_BLOCK_RESULTS_SLOT_REQUEST,
		};
	},

	// IDs are student IDs.
	reportBookTestResults(bookHash) {
		return {
			keyList: ["report", "blockResults", "bookTests", bookHash],
			payload: {miscBlockName: "bookTest" + bookHash},
			payloadIDsKey: "studentIDs",
			url: "/api/report/getBlockResults",
			action: AT.LOAD_REPORT_BOOK_TEST_RESULTS_SLOT_REQUEST,
		};
	},

	// Gets all block names for a chapter
	// IDs are chapter IDs
	chapterBlocks() {
		return {
			keyList: ["report", "blocksByChapter"],
			payloadIDsKey: "chapterIDs",
			url: "/api/report/getBlocks",
			action: AT.LOAD_CHAPTER_BLOCKS_SLOT_REQUEST,
		};
	},

	// Gets all assigned blocks for a given class
	// IDs are class IDs
	assignedBlocks() {
		return {
			keyList: ["report", "assignedBlocks"],
			payloadIDsKey: "classIDs",
			url: "/api/report/getAssignedBlocks",
			action: AT.LOAD_ASSIGNED_BLOCKS_FOR_CLASS_REQUEST,
		};
	},

	// Gets all lab blocks by a type
	// IDs are type IDs
	labBlocks() {
		return {
			keyList: ["report", "labBlocksByType"],
			payloadIDsKey: "typeIDs",
			url: "/api/report/getLabBlocks",
			action: AT.LOAD_TYPE_LAB_BLOCKS_SLOT_REQUEST,
		};
	},

	reportLabTypes() {
		return {
			keyList: ["report", "labTypes"],
			payloadIDsKey: "studentIDs",
			url: "/api/report/getLabTypes",
			action: AT.LOAD_REPORT_LAB_TYPES_SLOT_REQUEST,
		};
	},

	reportLabBlockResults(typeID) {
		const keyList = ["report", "labBlockResults", typeID];
		return {
			keyList,
			payload: {typeID},
			payloadIDsKey: "studentIDs",
			url: "/api/report/getLabBlockResults",
			action: AT.LOAD_REPORT_LAB_BLOCK_RESULTS_SLOT_REQUEST,
		};
	},
};

// Internal helper.
const makeDefaultObject = function(mslot) {
	return {
		status: "none",
		data: mslot.defaultValue || null,
		error: null,
	};
};

// Internal helper to work around the lack of a deep extend in ImmHelpers.
// makeNewValue is an (oldValue, id) => newValue function.
const extendWithIDs = function(state, mslot, ids, makeNewValue) {
	return CR(state)
		.at(mslot.keyList)
		.canCreate()
		.set(slotState => {
			const newSlotState = {};
			const defaultObj = makeDefaultObject(mslot);
			ids.forEach(id => {
				const orig = getIn(slotState, [id], defaultObj);
				newSlotState[id] = makeNewValue(orig, id);
			});
			return extendIn(slotState, [], newSlotState);
		});
};

// Set up the mslot for loading.
const init = function(state, mslot) {
	return CR(state)
		.at(mslot.keyList)
		.set({});
};

// Get loading info about the given ID. Returns {status, data, error}
const getInfo = function(state, mslot, id) {
	return CR(state)
		.at(mslot.keyList)
		.getIn([id], makeDefaultObject(mslot));
};

// Returns a single {load, loadError} object giving the effective status of the
// given IDs for use in a load guard component.
// TODO: make a new load guard that knows how this file works and remove this?
const getOldStyleStatus = function(state, mslot, ids) {
	// We return the status among all ids that appears last in this list.
	if (!ids || !ids.length) {
		return {load: "done"};
	}
	const priorityList = ["done", "pending", "error", "none"];
	const maxPriority = _.max(
		ids.map(id => priorityList.indexOf(getInfo(state, mslot, id).status))
	);

	const finalStatus = {
		load: priorityList[maxPriority] || "none",
		loadError: null,
	};
	ids.forEach(id => {
		const info = getInfo(state, mslot, id);
		finalStatus.loadError = finalStatus.loadError || info.error || null;
	});
	if (finalStatus.load === "error") {
		const firstErroredID = _.find(
			ids,
			id => getInfo(state, mslot, id).status === "error"
		);
		finalStatus.loadError = getInfo(state, mslot, firstErroredID).error;
	}
	return finalStatus;
};

// Get data for several objects at once. Returns an object mapping IDs to the
// corresponding data values. The fourth parameter is an optional function that
// can modify the returned data, taking (data, id).
const getDataMany = function(state, mslot, ids, mapper) {
	mapper = mapper || (x => x);
	return _.zipObject(
		ids,
		ids.map(id => mapper(getInfo(state, mslot, id).data, id))
	);
};

// Start loading one ID if it hasn't started or finished already. loadFunc
// takes (slot, ids) and starts the backend request. Typically you'd pass
// vault.api.loadMultiSlot. Returns the modified state.
const startLoadOne = function(state, mslot, id, loadFunc) {
	return startLoadMany(state, mslot, [id], loadFunc);
};

// Start loading any IDs in the given array that haven't started or finished
// already. loadFunc takes (slot, ids) and starts the backend request.
// Typically you'd pass vault.api.loadMultiSlot. Returns the modified state.
// This function was later added to check if needsReload is set in data.
const startLoadMany = function(state, mslot, ids, loadFunc) {
	if (!mslot) return state;
	const idsToLoad = ids.filter(id => {
		const {data, status} = getInfo(state, mslot, id);
		return (
			status === "none" ||
			status === "error" ||
			(_.get(data, "needsReload") && status === "done")
		);
	});
	if (idsToLoad.length) {
		loadFunc(mslot, idsToLoad);
		return extendWithIDs(state, mslot, idsToLoad, old =>
			extendIn(old, [], {status: "pending", error: null})
		);
	}
	return state;
};

// Set loaded data for an array of IDs. Returns the modified state. makeNewData is one of:
// - a function of the form (oldData, id) => newData.
// - an object with keys from ids and values the new data to extend the old
//   data with, intended as a convenient shorthand.
const setData = function(state, mslot, ids, makeNewData) {
	if (!makeNewData) {
		return state;
	} else if (typeof makeNewData !== "function") {
		const origMakeNewData = makeNewData;
		makeNewData = (old, id) => extendIn(old, [], origMakeNewData[id]);
	}
	return extendWithIDs(state, mslot, ids, (old, id) =>
		extendIn(old, [], {
			status: "done",
			data: makeNewData(old.data, id),
			error: null,
		})
	);
};

// Set a load error for an array of IDs. error is a fixed value, not a function.
// Returns the modified state.
const setError = function(state, mslot, ids, error) {
	return extendWithIDs(state, mslot, ids, (old, id) =>
		extendIn(old, [], {status: "error", data: null, error})
	);
};

// Mark the provided IDs as needing a reload by clearing all state for them. ID
// can either be a non-array (single ID) or an array of IDs. Returns the
// modified state. This does not start loading these
const clearSome = function(state, mslot, ids) {
	return CR(state)
		.at(mslot.keyList)
		.set(old => {
			if (!Array.isArray(ids)) {
				ids = [ids];
			}
			if (ids.some(id => old[id])) {
				return _.omit(old, ids);
			}
			return old;
		});
};

// Clear the entire mslot and mark all IDs for reloading. Returns the modified
// state.
const clearAll = function(state, mslot) {
	return init(state, mslot);
};

const getFormattedData = function(raw) {
	const data = {};
	_.each(raw, (c, id) => {
		if (!_.isEmpty(c.data)) data[id] = c.data;
	});
	return data;
};

module.exports = {
	table,
	init,
	getInfo,
	getDataMany,
	getOldStyleStatus,
	startLoadOne,
	startLoadMany,
	setData,
	setError,
	clearSome,
	clearAll,
	getFormattedData,
};
